import {
  FormControl,
  InputGroup,
  Input,
  InputRightAddon,
  Flex,
} from '@chakra-ui/react';
import {
  DimensionValue,
  DimensionData,
} from '@texas/api/endpoints/compositionApi';
import { setValueAsDecimalExtender } from '@texas/components/article/variant/sales/shared';
import {
  useAutoUpdateForm,
  AutoUpdateWrapper,
} from '@texas/components/shared/autoUpdate/useAutoUpdateForm';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { metricShortName } from './translations';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { Metric } from '@texas/api/endpoints/metadata/dimensionGroupsApi';
import {
  AutoUpdateMatrixSizes,
  AutoUpdateMatrixSizesAdditionalData,
  compositionGroupMatrixApi,
} from '@texas/api/endpoints/compositionGroup/compositionGroupMatrixApi';

export function MatrixDimensionsForm({
  compositionId,
  controllerId,
  compositionIndex,
  matrixNodeId,
  dimension,
  onUpdate,
}: {
  compositionId: number;
  controllerId: number;
  compositionIndex: number;
  matrixNodeId: number;
  dimension: DimensionValue;
  onUpdate?: (
    compositionId: number,
    dimensionId: number,
    value: number,
  ) => void;
}) {
  const {
    register,
    registerAutoUpdate,
    triggerSubmit,
    form,
    formState: { errors },
  } = useAutoUpdateForm<
    AutoUpdateMatrixSizes,
    DimensionData,
    AutoUpdateMatrixSizesAdditionalData
  >({
    defaultValue: {
      value: dimension.value,
    },
    requestParams: [controllerId],
    namePrefix: 'dimension',
    additionalData: {
      dimensionId: dimension.id,
      compositionIndex: compositionIndex,
      matrixNodeId: matrixNodeId,
    },
    autoUpdateRequest: compositionGroupMatrixApi.autoUpdateSizes,
    onResponse(data) {
      if (!onUpdate) return;
      onUpdate(compositionId, dimension.id, data.mappedServerData.value);
    },
  });

  return (
    <form {...form}>
      <FormControl isInvalid={!!errors.value?.message}>
        {(dimension.identifier ?? []).length > 0 ? (
          <Flex gap={1} mb={0} align="baseline">
            <TexasFormLabel mr={0} fontWeight="bold">
              {dimension.identifier}:
            </TexasFormLabel>
            <TexasFormLabel>{dimension.label}</TexasFormLabel>
          </Flex>
        ) : (
          <TexasFormLabel>{dimension.label}</TexasFormLabel>
        )}

        <InputGroup>
          <AutoUpdateWrapper autoUpdateProps={registerAutoUpdate} path="value">
            <Input
              {...register('value', {
                onBlur: triggerSubmit,
                setValueAs: (value) => setValueAsDecimalExtender(value, true),
              })}
              variant="texas"
            />
          </AutoUpdateWrapper>
          <InputRightAddon h="auto" border="none">
            {metricShortName(ensureEnumNumber(Metric, dimension.metric))}
          </InputRightAddon>
        </InputGroup>
        <ErrorLabel text={errors.value?.message} />
      </FormControl>
    </form>
  );
}
